import { useFocusSignal } from "@/hooks/useFocusSignal";
import { useState, useRef, useEffect } from "react";

type InlineEditableProps = {
  value?: string;
  placeholder?: string;
  onSave: (next: string) => void | Promise<void>;
  className?: string;
  dir?: "ltr" | "rtl";
  startEditing?: boolean;
  enableClickToEdit?: boolean;
};

export function InlineEditableInput({
  value = "",
  placeholder = "...",
  onSave,
  className,
  dir = "ltr",
  startEditing = false,
  enableClickToEdit = true,
}: InlineEditableProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const savedRef = useRef(false);
  const { ref: inputRef, trigger: focusInput } =
    useFocusSignal<HTMLInputElement>();

  useEffect(() => {
    if (editing) {
      setDraft(value ?? "");
    }
  }, [editing, value]);

  useEffect(() => {
    if (startEditing) {
      setEditing(true);
      focusInput();
    }
  }, [startEditing, focusInput]);

  const commit = async () => {
    if (saving || savedRef.current) return;
    savedRef.current = true;
    setSaving(true);
    try {
      if (draft !== value) {
        await onSave(draft.trim());
      }
    } finally {
      setSaving(false);
      setEditing(false);
      setTimeout(() => (savedRef.current = false), 0);
    }
  };

  const cancel = () => {
    setEditing(false);
    setDraft(value ?? "");
  };

  if (!editing) {
    return (
      <span
        className={className}
        onClick={() => {
          if (!enableClickToEdit) return;
          setEditing(true);
          focusInput();
        }}
        title={enableClickToEdit ? "Click to edit" : undefined}
        dir={dir}
      >
        {value?.trim() || placeholder}
      </span>
    );
  }

  return (
    <input
      ref={inputRef}
      dir={dir}
      className={`${className} focus:outline-none`}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        } else if (e.key === "Escape") {
          e.preventDefault();
          cancel();
        }
      }}
      disabled={saving}
    />
  );
}
